import {DestroyRef, Injectable, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  tap,
  throwError,
} from 'rxjs';
import {
  Device,
  IDevice,
} from 'src/app/core/entities/device/models/device.model';
import {SOCKET_DEVICE_EVENT} from '../../enums/events-buffer/socket/socket-device-event.enum';
import {SOCKET_ROOM} from '../../enums/events-buffer/socket/socket-rooms-enum';
import {Logger} from '../../interfaces/Logger';
import {SocketErrorResponse} from '../../interfaces/socket-error.model';
import {ISocketPayload} from '../../interfaces/socket-payload.interface';
import {SocketRoomHandlerService} from '../../services/socket/socket-room-handler.service';
import {SocketService} from '../../services/socket/socket.service';
import {SignatureService} from '../service/signature.service';
import {DeviceService} from './device.service';
import {EVENT_BUS} from '@shared/domain/tokens/event-bus.token';
import {ServiceRemovedDeviceEvent} from '@platform/event-bus/domain/events/service-removed-device.event';

@Injectable({providedIn: 'root'})
export class DeviceSocketHandlerService {
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly logger = inject(Logger);
  private readonly socketService = inject(SocketService);
  private readonly deviceService = inject(DeviceService);
  private readonly signatureService = inject(SignatureService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly eventBus = inject(EVENT_BUS);
  private destroyed$: Subject<void> = new Subject<void>();

  constructor() {
    this.initDevicesRoomListener();
  }

  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Listens for events indicating entry into the device room.
   * Initializes listeners for device events when a device room entry event is received.
   */
  private initDevicesRoomListener(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.devices)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeDeviceListeners();
      });
  }

  /**
   * Initializes listeners for device-related events.
   * This method sets up listeners for device removal, addition, activation, and unlinking.
   */
  private initializeDeviceListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Device listeners initialized]`
    );
    this.onDeviceRemovedListener();
    this.onDeviceAddedListener();
    this.onDeviceActivatedListener();
    this.onDeviceUnlinkListener();
  }

  /**
   * Initializes a listener for device removal events.
   * This method sets up a listener to receive notifications when a device is removed.
   * Upon receiving a device removal event, it inserts the signature checksum (if available) into the database
   * and inserts device data into IndexedDB. Then, it notifies the device service about the device removal.
   */
  private onDeviceRemovedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Device removed listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DEVICE_EVENT.removed)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const device = new Device(eventResponse.payload.device as IDevice);
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (device) {
              return combineLatest([
                signature$,
                this.deviceService.insertDevice(device),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.devices} 📱] [Device removed socket recieved]`
        );
        this.deviceService.notifyOnDeviceRemoved();
        this.eventBus.publish(new ServiceRemovedDeviceEvent());
      });
  }

  /**
   * Initializes a listener for device addition events.
   * This method sets up a listener to receive notifications when a device is added.
   * Upon receiving a device addition event, it inserts the signature checksum (if available) into the database
   * and inserts device data into IndexedDB. Then, it notifies the device service about the device addition.
   */
  private onDeviceAddedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Device added listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DEVICE_EVENT.added)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const device = new Device(eventResponse.payload.device as IDevice);
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (device) {
              return combineLatest([
                signature$,
                this.deviceService.insertDevice(device),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.devices} 📱] [Device added socket recieved]`
        );
        this.deviceService.notifyOnDeviceAdded();
      });
  }

  /**
   * Initializes a listener for device activation/deactivation events.
   * This method sets up a listener to receive notifications when a device is activated or deactivated.
   * Upon receiving a device activation/deactivation event, it inserts the signature checksum (if available) into the database
   * and inserts device data into IndexedDB. Then, it notifies the device service about the device activation/deactivation.
   */
  private onDeviceActivatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Device activated/deactivated listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DEVICE_EVENT.activated)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const device = new Device(eventResponse.payload.device as IDevice);
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (device) {
              return combineLatest([
                signature$,
                this.deviceService.insertDevice(device),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.devices} ] [Device activated/deactivated socket recieved]`
        );
        this.deviceService.notifyOnDeviceActivated();
      });
  }

  /**
   * Initializes a listener for device unlink events.
   * This method sets up a listener to receive notifications when a device is unlinked.
   * Upon receiving a device unlink event, it inserts the signature checksum (if available) into the database
   * and notifies the device service about the device being unlinked.
   */
  private onDeviceUnlinkListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.devices} 📱] [Device unlink listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_DEVICE_EVENT.unlink)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) => {
          const device = new Device(eventResponse.payload.device as IDevice);
          const signature = eventResponse.payload?.signature?.checksum;

          let signature$ = of('');

          if (signature) {
            signature$ = this.signatureService.insertSignatureOnDB(signature);
          }

          if (device) {
            return combineLatest([
              signature$,
              this.deviceService.insertDevice(device),
            ]);
          }

          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.devices} ] [Device unlink socket recieved]`
        );
        this.deviceService.notifyOnDeviceUnlinked();
      });
  }
}
